/**
 * Lab environment (container image) catalogue — pure helpers.
 *
 * A lab records two things: `containerConfig.image` is what RUNS, and
 * `containerConfig.imageKey` is what was CHOSEN. The catalogue is the set of
 * choices, keyed by a stable slug (`key`); the UI binds to that key and never
 * to an image reference, because a free-text image on a faculty-reachable
 * endpoint would be arbitrary container execution on a cluster whose service
 * account can exec into any pod in the `labs` namespace.
 *
 * Everything here is pure and deliberately tolerant, for three reasons:
 *
 *  1. Every lab created before the catalogue existed carries
 *     `image: 'lab-environment:latest'` and `imageKey: null`. Those labs must
 *     resolve to the platform-default entry, not read as "unrecognised" — or a
 *     faculty member saving an unrelated setting would be invited to "fix" a
 *     value that was never broken.
 *  2. The backend serving the catalogue deploys independently of this bundle,
 *     so the UI has to stay usable when the endpoint is not there yet.
 *  3. Field names drift between an API and its client. Entries are normalised
 *     once, here, rather than read shape-first in three components.
 */

/**
 * What the platform runs when a lab has expressed no preference. Used as the
 * entire catalogue when the API is unreachable, so the picker can still state
 * the truth ("everyone is on the standard environment") instead of erroring.
 */
export const FALLBACK_DEFAULT_ENTRY = Object.freeze({
  key: 'default',
  name: 'Standard lab environment',
  description: 'Python, Java, C/C++, Node and the usual course tooling.',
  image: null,
  resolvedImage: null,
  toolchains: [],
  suggestedFor: [],
  suggestedMemory: null,
  suggestedMemoryRequest: null,
  suggestedCpu: null,
  suggestedCpuLimit: null,
  starterCode: { normalizeJavaProject: true, pruneBinDirs: 'always', javaRelease: 21 },
  isDefault: true,
  retired: false,
  sortOrder: 0,
  labCount: null,
  synthetic: true,
});

/** Drop a registry host so two spellings of one image compare equal. */
export function stripRegistry(ref) {
  if (!ref || typeof ref !== 'string') return '';
  const trimmed = ref.trim();
  if (!trimmed) return '';
  const parts = trimmed.split('/');
  // The first segment is a registry host only when it looks like one.
  if (parts.length > 1 && (parts[0].includes('.') || parts[0].includes(':') || parts[0] === 'localhost')) {
    parts.shift();
  }
  return parts.join('/');
}

function toUpperList(value) {
  let list = value;
  if (typeof list === 'string') list = list.split(/[,\s]+/);
  if (!Array.isArray(list)) return [];
  return list.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean);
}

function toList(value) {
  let list = value;
  if (typeof list === 'string') list = list.split(/[,\s]+/);
  if (!Array.isArray(list)) return [];
  return list.map((c) => String(c || '').trim()).filter(Boolean);
}

/** Normalise one API entry into the single shape the UI reads. */
export function normalizeEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const key = raw.key || raw.slug || raw.id || raw._id;
  if (!key) return null;

  const res = raw.suggestedResources || {};

  // Retirement may arrive as a flag or as a status string. Any of them means
  // the same thing here: not offered to new labs.
  const status = typeof raw.status === 'string' ? raw.status.toLowerCase() : null;
  const retired = raw.retired ?? raw.isRetired ?? (
    status ? (status === 'retired' || status === 'archived' || status === 'disabled')
      : (raw.isActive === false)
  );

  const labCountRaw = raw.labCount ?? raw.labsUsing ?? raw.usageCount ?? raw.usedByLabs;
  const labCount = (labCountRaw !== null && labCountRaw !== undefined && labCountRaw !== ''
    && Number.isFinite(Number(labCountRaw)))
    ? Number(labCountRaw)
    : null;

  return {
    key: String(key),
    name: raw.displayName || raw.name || raw.label || String(raw.image || key),
    description: raw.description || raw.summary || '',
    image: raw.image || raw.imageRef || raw.ref || null,
    // What the platform default actually points at, when the entry's own
    // reference is the unqualified sentinel.
    resolvedImage: raw.resolvedImage || null,
    toolchains: toList(raw.toolchains || raw.languages),
    suggestedFor: toUpperList(raw.suggestedFor ?? raw.courseCodes ?? raw.suggestedCourses),
    // The ceiling is what matters to a student: exceeding it ends their session.
    suggestedMemory: res.memoryLimit || raw.suggestedMemory || raw.recommendedMemory || null,
    suggestedMemoryRequest: res.memory || null,
    suggestedCpu: res.cpus || res.cpu || null,
    suggestedCpuLimit: res.cpuLimit || null,
    starterCode: {
      normalizeJavaProject: raw.starterCode?.normalizeJavaProject !== false,
      pruneBinDirs: raw.starterCode?.pruneBinDirs || 'always',
      javaRelease: raw.starterCode?.javaRelease || 21,
    },
    isDefault: Boolean(raw.isDefault ?? raw.default ?? false),
    retired: Boolean(retired),
    sortOrder: Number.isFinite(Number(raw.sortOrder)) ? Number(raw.sortOrder) : 100,
    labCount,
    synthetic: false,
  };
}

/** Normalise a whole API response (array, or {images|labImages|catalog|entries|data}). */
export function normalizeCatalog(response) {
  const list = Array.isArray(response)
    ? response
    : (response?.images || response?.labImages || response?.catalog
      || response?.entries || response?.data || []);
  if (!Array.isArray(list)) return [];
  return list.map(normalizeEntry).filter(Boolean);
}

/** Unwrap a single-entry response ({image}|{labImage}|{entry}|{data}|bare). */
export function normalizeEntryResponse(response) {
  if (!response || typeof response !== 'object') return null;
  const raw = response.image && typeof response.image === 'object' ? response.image
    : (response.labImage || response.entry || response.data || response);
  return normalizeEntry(raw);
}

/** The entry a lab gets when it has expressed no preference. */
export function findDefaultEntry(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return list.find((e) => e.isDefault && !e.retired)
    || list.find((e) => e.isDefault)
    || list.find((e) => !e.retired)
    || list[0]
    || FALLBACK_DEFAULT_ENTRY;
}

/** Entries a faculty member may newly choose (retired ones are never offered). */
export function selectableEntries(entries) {
  return (Array.isArray(entries) ? entries : []).filter((e) => !e.retired);
}

/** Display order: the catalogue's own sortOrder, then name. Never a DB sort — see the model. */
export function orderEntries(entries) {
  return [...(Array.isArray(entries) ? entries : [])].sort((a, b) => {
    if (a.retired !== b.retired) return a.retired ? 1 : -1;
    const order = (a.sortOrder ?? 100) - (b.sortOrder ?? 100);
    if (order !== 0) return order;
    return String(a.name).localeCompare(String(b.name));
  });
}

/**
 * The synthetic option shown when a lab points at something the catalogue does
 * not offer. Rendering it is what stops the picker from silently reassigning
 * the lab to the default on the next save.
 */
export function unknownEntryFor({ image, imageKey }) {
  const ref = imageKey || image;
  return {
    ...FALLBACK_DEFAULT_ENTRY,
    key: `__unknown__${ref}`,
    name: 'Not in the catalogue',
    description: String(image || imageKey || 'unknown'),
    image: image || null,
    isDefault: false,
    retired: true,
    synthetic: true,
    unknown: true,
  };
}

/**
 * Resolve a lab's stored containerConfig to a catalogue entry.
 *
 * Mirrors the server's own lookup order (services/LabImageCatalogue.entryForLab):
 * key first, then the image reference, then the default. The registry-stripped
 * comparison is a safety net for a cluster whose catalogue publishes a fully
 * qualified ref where the lab stored an unqualified one.
 *
 * Returns null when `entries` is empty (catalogue unavailable) so callers can
 * tell "no catalogue" apart from "unrecognised image".
 */
export function matchEntryForLab(entries, containerConfig) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length === 0) return null;

  const cfg = containerConfig || {};
  const key = typeof cfg.imageKey === 'string' ? cfg.imageKey.trim().toLowerCase() : '';
  if (key) {
    const byKey = list.find((e) => String(e.key).toLowerCase() === key);
    if (byKey) return byKey;
  }

  const ref = typeof cfg.image === 'string' ? cfg.image.trim() : '';
  if (!ref) return key ? unknownEntryFor(cfg) : findDefaultEntry(list);

  const exact = list.find((e) => e.image && e.image === ref);
  if (exact) return exact;

  const bare = stripRegistry(ref);
  const stripped = list.find((e) => e.image && stripRegistry(e.image) === bare);
  if (stripped) return stripped;

  const resolved = list.find((e) => e.resolvedImage && e.resolvedImage === ref);
  if (resolved) return resolved;

  return unknownEntryFor(cfg);
}

/**
 * Pick the entry suggested for a course code.
 *
 * An exact course-code match always wins. Otherwise the longest matching prefix
 * wins, so a catalogue can carry both a broad 'INFS' hint and a specific
 * 'INFS4102' one without the broad one shadowing it. Retired entries are never
 * suggested.
 */
export function suggestEntryForCourse(entries, courseCode) {
  const code = String(courseCode || '').trim().toUpperCase();
  if (!code) return null;

  const list = selectableEntries(entries).filter((e) => e.suggestedFor.length > 0);
  if (list.length === 0) return null;

  const exact = list.find((e) => e.suggestedFor.includes(code));
  if (exact) return exact;

  let best = null;
  let bestLen = 0;
  for (const entry of list) {
    for (const hint of entry.suggestedFor) {
      if (hint.length >= 2 && code.startsWith(hint) && hint.length > bestLen) {
        best = entry;
        bestLen = hint.length;
      }
    }
  }
  return best;
}

/**
 * Parse a memory string to MiB.
 *
 * Follows the backend's reading (KubernetesProvider.toK8sMemory): a bare or
 * lowercase suffix is Docker syntax and resolves as binary, so '512m' is 512
 * MiB, NOT 512 milli-bytes. Matching that here is the point — a warning that
 * disagrees with what the cluster does is worse than no warning.
 *
 * Returns null for anything unparseable rather than guessing.
 */
export function parseMemoryToMiB(value) {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str) return null;

  const m = /^(\d+(?:\.\d+)?)\s*([a-zA-Z]{0,2})$/.exec(str);
  if (!m) return null;

  const num = parseFloat(m[1]);
  if (!Number.isFinite(num)) return null;

  switch (m[2].toLowerCase()) {
    case '': return num / (1024 * 1024); // a bare number is bytes
    case 'k': case 'ki': return num / 1024;
    case 'm': case 'mi': return num;
    case 'g': case 'gi': return num * 1024;
    case 't': case 'ti': return num * 1024 * 1024;
    default: return null;
  }
}

/** Human-readable memory, e.g. '4Gi' -> '4 GiB'. Passes unparseable values through. */
export function formatMemory(value) {
  const mib = parseMemoryToMiB(value);
  if (mib === null) return String(value ?? '');
  if (mib >= 1024 && mib % 1024 === 0) return `${mib / 1024} GiB`;
  if (mib >= 1024) return `${(mib / 1024).toFixed(1)} GiB`;
  return `${Math.round(mib)} MiB`;
}

/**
 * Does this environment want a higher memory ceiling than the lab allows?
 *
 * Returns null when there is nothing to say — no suggestion, unparseable
 * values, or the lab already allows enough. Otherwise returns both numbers so
 * the warning can name them.
 */
export function memoryShortfall(entry, configuredMemory) {
  const want = parseMemoryToMiB(entry?.suggestedMemory);
  const have = parseMemoryToMiB(configuredMemory);
  if (want === null || have === null) return null;
  if (want <= have) return null;
  return {
    suggested: entry.suggestedMemory,
    configured: configuredMemory,
    suggestedMiB: want,
    configuredMiB: have,
  };
}

/** Short label for a ref, for the places that show the raw image. */
export function shortImageRef(ref) {
  const bare = stripRegistry(ref);
  return bare || String(ref || '');
}

/**
 * Should the environment's own memory ceiling take effect on this save?
 *
 * The server applies a catalogue entry's suggested resources only to the fields
 * a request leaves unset, and only when the image actually changes. So the lab
 * form has to choose between "keep the ceiling shown on screen" and "let the
 * environment size itself". It lets the environment size itself exactly when the
 * operator picked a new environment and did not touch the memory buttons — which
 * is also the only way to reach a ceiling the buttons do not offer.
 */
export function shouldAdoptSuggestedMemory({
  imageChanged, memoryTouched, selectedEntry, memoryLimit,
}) {
  if (!imageChanged || memoryTouched) return false;
  const want = parseMemoryToMiB(selectedEntry?.suggestedMemory);
  if (want === null) return false;
  return want !== parseMemoryToMiB(memoryLimit);
}

/**
 * The `containerConfig` body for a lab update.
 *
 * THE rule this whole feature rests on: the image is sent only when a human
 * deliberately changed it. Every lab in production stores
 * 'lab-environment:latest'; echoing that field back on every save — including
 * saves that were only about a due date — would rewrite the runtime of live
 * classes as a side effect of an unrelated edit.
 *
 * It is sent as the catalogue KEY, never as an image reference: the key is the
 * choice, and the server refuses any key not in the catalogue. Sending a
 * reference would make the client the thing deciding what actually runs.
 *
 * A memory button sets the RESERVATION as well as the ceiling. A student pod
 * reserves all the memory it may use, so a busy node can never squeeze it
 * mid-class; and the ceiling alone would be refused whenever it fell below the
 * stored reservation (2 GiB chosen on a lab reserving 3Gi). An untouched save
 * sends the ceiling only, so it never rewrites a reservation nobody changed.
 */
export function buildContainerConfigUpdate({
  memoryLimit, memoryTouched = false, imageChanged, selectedEntry, adoptsSuggestedMemory,
}) {
  const memory = memoryTouched ? { memory: memoryLimit, memoryLimit } : { memoryLimit };
  // No change, or nothing valid to send: behave exactly as the form did before
  // image selection existed.
  if (!imageChanged || !selectedEntry?.key || selectedEntry.synthetic) {
    return memory;
  }
  return {
    imageKey: selectedEntry.key,
    // Omitted on purpose when the environment's own sizing should apply.
    ...(adoptsSuggestedMemory ? {} : memory),
  };
}
